import React from 'react';
import {
  CalendarDays,
  Download,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';

interface PassbookToolbarProps {
  searchInput: string;
  dateFrom: string;
  dateTo: string;
  loading?: boolean;
  onSearchInputChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onApply: () => void;
  onReset: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

const PassbookToolbar: React.FC<PassbookToolbarProps> = ({
  searchInput,
  dateFrom,
  dateTo,
  loading = false,
  onSearchInputChange,
  onDateFromChange,
  onDateToChange,
  onApply,
  onReset,
  onRefresh,
  onExport,
}) => {
  const hasFilters = Boolean(
    searchInput.trim() ||
      dateFrom ||
      dateTo,
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onApply();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(280px,1fr)_180px_180px_auto]">
        <div>
          <label
            htmlFor="passbook-search"
            className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500"
          >
            Search Ledger
          </label>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              id="passbook-search"
              type="search"
              value={searchInput}
              disabled={loading}
              onChange={(event) =>
                onSearchInputChange(event.target.value)
              }
              placeholder="Transaction ID, market, bet type..."
              className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="passbook-date-from"
            className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500"
          >
            From Date
          </label>

          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              id="passbook-date-from"
              type="date"
              value={dateFrom}
              disabled={loading}
              onChange={(event) =>
                onDateFromChange(event.target.value)
              }
              className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="passbook-date-to"
            className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500"
          >
            To Date
          </label>

          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              id="passbook-date-to"
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              disabled={loading}
              onChange={(event) =>
                onDateToChange(event.target.value)
              }
              className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Apply
          </button>

          <button
            type="button"
            disabled={loading || !hasFilters}
            onClick={onReset}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            Reset
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onRefresh}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onExport}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            Export Page CSV
          </button>
        </div>
      </div>
    </form>
  );
};

export default PassbookToolbar;
